import React from 'react';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import { formatDateShort } from '../../utils/dateFormat';
// GPS verdict of a QR scan — the shared badge.
import VenueCheckBadge from './VenueCheckBadge';

// Presentational — a sub-tab of Participation Records: the rows arrive
// already run through the shared filters (search / session / date range)
// owned by ParticipationRecords, which also owns the fetch — the data itself
// comes from LDDashboard's existing /api/ld/attendance load, so this table
// does not request it a second time.
//
// `onShowLocation(recordId)` is optional and comes from the container: given,
// the Venue Check badge becomes a button onto the evidence panel; omitted, it
// stays a plain badge. The container decides, because the endpoint behind that
// panel is `ld:training:edit` and this tab is only `ld:view`.

const Attendance = ({ records, onShowLocation = null }) => {
  const { rows, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(records);

  // Calculate statistics
  const totalAttendance = records.length;
  const totalHours = records.reduce((sum, r) => sum + (r.trainingHours || 0), 0);
  const trainerHours = records.reduce((sum, r) => sum + (r.trainerHours || 0), 0);
  const uniqueParticipants = new Set(records.map(r => r.email)).size;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Total Records</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{totalAttendance}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Unique Participants</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{uniqueParticipants}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Total Training Hours</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{totalHours.toFixed(1)}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Trainer Hours</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{trainerHours.toFixed(1)}</p>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Email" sortKey="email" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Training Session" sortKey="courseTitle" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Training Hours" sortKey="trainingHours" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Trainer Hours" sortKey="trainerHours" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                {/* The GPS verdict. Sorting groups the "Outside venue"
                    rows; false sorts before true, nulls sink to the end. */}
                <SortableHeader label="Venue Check" sortKey="checkinInRange" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No attendance records found
                  </td>
                </tr>
              ) : (
                rows.map((record, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {record.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{record.courseTitle}</div>
                      <div className="text-xs text-gray-500">ID: {record.sessionCode}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDateShort(record.date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {record.trainingHours != null ? Number(record.trainingHours).toFixed(2) : ''} hrs
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        {record.trainerHours != null ? Number(record.trainerHours).toFixed(2) : 0} hrs
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* Clickable on the same terms as the management list
                          — but only when the container
                          hands down onShowLocation, which it does solely for
                          readers who also hold ld:training:edit. Where someone
                          stood is still not for every ld:view reader. */}
                      <VenueCheckBadge
                        value={record.checkinInRange}
                        distanceM={record.checkinDistanceM}
                        radiusM={record.checkinRadiusM}
                        onClick={onShowLocation && record.id != null
                          ? () => onShowLocation(record.id)
                          : null}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePager
          page={page}
          setPage={setPage}
          total={total}
          className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50"
        />
      </div>
    </div>
  );
};

export default Attendance;
